import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { identifyUserEvent } from '@/utils/account'
import { getCookieDomain } from '@/utils/cookie'
import { datadogRum } from '@datadog/browser-rum'

interface LoginCredentials {
  username: string
  password: string
  url?: string
  disableUserFetching?: boolean
  disableRedirect?: boolean
}

interface LoginResponse {
  token: string
  token_expires_at: string
}

interface AuthApi {
  login: (params: LoginCredentials) => Promise<LoginResponse>
  setUserToken: (token: string) => void
  logout: (params?: { redirectUrl?: string; keepToken?: boolean }) => void
}

export default defineNuxtPlugin((nuxtApp) => {
  const { fetchUser } = useAuth()

  const authApi: AuthApi = {
    login: async ({ username, password, url, disableUserFetching = false, disableRedirect = false }) => {
      const authStore = useAuthStore()
      const appStore = useAppStore()

      const response = await (authStore as any).login({ username, password })

      const apolloToken = useCookie('apollo-token', {
        path: '/',
        expires: new Date(Date.now() + 432000),
        maxAge: 60 * 60 * 24 * 5,
        domain: getCookieDomain()
      })
      apolloToken.value = response.token

      const apolloExpires = useCookie('apollo-expires', {
        path: '/',
        expires: new Date(Date.now() + 432000),
        maxAge: 60 * 60 * 24 * 5,
        domain: getCookieDomain()
      })
      apolloExpires.value = response.token_expires_at

      if (!disableUserFetching) {
        try {
          const user = await fetchUser()

          if (user?.user?._id) {
            identifyUserEvent({ otaUserId: `${user?.user?._id}` })
          }
        } catch (_error) {
          const apolloToken = useCookie('apollo-token', { path: '/', domain: getCookieDomain() })
          apolloToken.value = null

          const apolloExpires = useCookie('apollo-expires', { path: '/', domain: getCookieDomain() })
          apolloExpires.value = null
        }
      }

      const route = useRoute()
      if (!route.query?.chatId && !disableRedirect) {
        const next = route.query?.next ? route.query.next : url
        navigateTo(`/${appStore.locale}-${appStore.country}${next}`)
      }

      if (authStore.redirectUrl !== '/account') {
        authStore.setRedirectUrl('/account')
      }

      return response
    },

    setUserToken: (token: string) => {
      const authStore = useAuthStore()

      const apolloToken = useCookie('apollo-token', {
        path: '/',
        expires: new Date(Date.now() + 432000),
        maxAge: 60 * 60 * 24 * 5,
        domain: getCookieDomain()
      })
      apolloToken.value = token

      authStore.setAuthToken(token, '')
    },

    logout: ({ redirectUrl = '/login', keepToken = false } = {}) => {
      const authStore = useAuthStore()
      const appStore = useAppStore()
      const userStore = useUserStore()

      if (!keepToken) {
        userStore.deleteSession().catch((deleteSessionError) => {
          useNuxtApp().$ota.general.logError(deleteSessionError)
        })
      }

      authStore.logout()

      const apolloExpires = useCookie('apollo-expires', { path: '/', domain: getCookieDomain() })
      apolloExpires.value = null
      const apolloToken = useCookie('apollo-token', { path: '/', domain: getCookieDomain() })
      apolloToken.value = null

      datadogRum.clearUser()

      navigateTo(`/${appStore.locale}-${appStore.country}${redirectUrl}`)
    }
  }

  nuxtApp.provide('auth', authApi)
})
